/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import mixpanel from 'mixpanel-browser';

import { PearlUser } from './Login/userSlice';
import { isMedPearlOne } from './permissions';
import { mixPanelToken } from '../../app/tokens';
import pearlApi from './api/pearlApi';
import GitCommit from '../../../src/_git_commit';

const environment = process.env.REACT_APP_ENVIRONMENT || 'development';
const isProduction = environment === 'production';
console.log('Mixpanel is in ' + environment + ' mode');
// Note, we set persistance to localStorage because otherwise it uses
// cookies, and this triggers AWS errors, giving a 403 error.
let enabled = false;
if (mixPanelToken) {
  if (!mixpanel) throw new Error('mixpanel not loaded');
  mixpanel.init(mixPanelToken, {
    debug: !isProduction,
    //debug: true, // Temporary for debugging
    ignore_dnt: true, // Ignore do-not-track which the vendor browser sets
    persistence: 'localStorage',
    api_host: 'https://mixpanel-proxy.pearl-project.io',
  });
  enabled = true;
}

/** Set this to a unique identifier for the user performing the event.
 *   eg: their ID in your database or their email address.
 */
export function analyticsIdentify(userId?: string) {
  enabled = !!userId;
  mixpanel.identify(userId);
}

// Whether this is creator, mobile, consumer, etc.  This can be set to whatever you want in your app
var isCreator_ = false;
/** Call once in your App code.  E.g. setAppName('creator') */
export function setCreator() {
  isCreator_ = true;
}

export function isCreator() {
  return isCreator_;
}

export type InsightsEventArgs = {
  eventType?: 'click' | 'conversion';
  eventName: 'Opened Document After Search';
  index: string;
  queryID: string;
  objectIDs: string[];
  positions?: number[];
};

function postInsightsEvent(eventData: InsightsEventArgs) {
  void pearlApi.post('/search/event', eventData);
}

/**
 *
 * STOP!
 *
 * Are you adding a new event?
 *
 * This should be documented at:
 * https://pearl-project.atlassian.net/wiki/spaces/PearlArchitecture/pages/158662658/Mixpanel+and+Pendo
 *
 * Before making changes to this, inform:
 *  Cynthia.Walsh@providence.org
 *  Prateek.Fulsundar@providence.org
 */
export function analyticsTrack(
  event: // See comment above before adding!
  | 'audience_change_consumer'
    | 'care_everywhere_panel'
    | 'copy_to_clipboard'
    | 'document action'
    | 'document_open'
    | 'external_link_click'
    | 'generate_patient_notes_summary'
    | 'guide_tabs'
    | 'legal_disclaimer_consumer_accepted'
    | 'npi_skipped_profession_submitted'
    | 'quick_start_guide_consumer_open'
    | 'search_multiple_results_then_click'
    | 'search_multiple_results'
    | 'search_no_result'
    | 'search'
    | 'summary_action'
    | 'timing'
    | 'welcomepage_widget_click'
    | 'widget_not_relevant_click'
    | 'musc_welcomepage_video_played'
    | 'ctx_widget_action'
    | 'login_initiated'
    | 'login_succeeded'
    | 'login_failed'
    | 'algo_question_toggled',
  properties: { [key: string]: any },
  pearlUserInfo: PearlUser | undefined,
  insightsEventInfo?: InsightsEventArgs
) {
  if (!enabled) return;
  // Track an event. It can be anything, but in this example, we're tracking a Signed Up event.
  // Include a property about the signup, like the Signup Type
  // mixpanel can't handle a field longer than 256 characters, so split it
  properties.session_id = pearlUserInfo?.sessionId;
  properties.sourceApp = pearlUserInfo?.sourceApp || 'WEB';
  properties.creator = isCreator_;
  console.log('analyticsTrack', event, properties);
  (window as any).incrementAnalyticsCount?.(event);
  mixpanel.track(event, properties);
  properties = { ...properties };
  if (properties.session_id) {
    delete properties.session_id;
  }
  if (properties.documentVersionId) {
    delete properties.documentVersionId;
  }
  if (
    event === 'document_open' &&
    !!insightsEventInfo &&
    !!insightsEventInfo.queryID &&
    insightsEventInfo.objectIDs.length > 0 &&
    !!insightsEventInfo.objectIDs[0]
  ) {
    postInsightsEvent({
      ...insightsEventInfo,
      eventType: 'click',
    });

    const { positions, ...rest } = insightsEventInfo;
    postInsightsEvent({
      ...rest,
      eventType: 'conversion',
    });
  }
}

/**
 *  Set the email, username etc for events.
 *
 *  As per 05 June 2024, we only use mixpanel. See: https://pearl-project.atlassian.net/browse/PRL-8471
 *  Before that we had pendo too.
 */
export function analyticsInitialize(pearlUserInfo: PearlUser) {
  enabled = true;
  let orgEnv = '';
  switch (environment.toLowerCase()) {
    case 'development':
      orgEnv = '-DEV';
      break;
    case 'test':
      orgEnv = '-TST';
      break;
  }
  const visitor = {
    id: pearlUserInfo.userId,
    email: pearlUserInfo.email || '(No email)',
    full_name: pearlUserInfo.name || '(No full_name)',
    role: pearlUserInfo.orgRole,
    // You can add any additional visitor level key-values here,
    // as long as it's not one of the above reserved names.
    // John T.: Additional key-values that I thought might be nice?
    first_name: pearlUserInfo.givenName || '(No first_name)',
    last_name: pearlUserInfo.familyName || '(No last_name)',
    title: pearlUserInfo.title || '(No title)',
    orgCode: (pearlUserInfo.orgCode || '(No orgCode)') + orgEnv,
    app_version: GitCommit.logMessage.toString() || 'No version found',
  };

  mixpanel.people.set({ ...visitor, org_id: pearlUserInfo.orgId || '(No orgId)' });
  // Set company to MED_PEARL_ONE for the users with role MED_PEARL_ONE
  const company = isMedPearlOne(pearlUserInfo) ? pearlUserInfo.orgRole : [pearlUserInfo.orgCode || '(No orgCode)'];
  mixpanel.set_group('company', company);
  mixpanel.set_group('org_role', pearlUserInfo.orgRole || '(No orgRole)');
}

/** Helper function to make sure we call this consistently
    Before making changes to this, inform:
    Cynthia.Walsh@providence.org
    Prateek.Fulsundar@providence.org

    If you modify this, please update the documentation at:
    https://pearl-project.atlassian.net/wiki/spaces/PearlArchitecture/pages/158662658/Mixpanel+and+Pendo

    The search_multiple_results_then_click event is fired when a user types a
    search term, no trigger matched, then they clicked a link.

    term: The search term they typed, clicked, etc
    persona: The persona they selected e.g. "Provder"
    careCategory: The care category they selected e.g. "Outpatient"
    patientGroup: The patient group they selected e.g. "Adult and Peds"
    ranking: The ranking of the result they clicked e.g. 1
    title: The title of the result they then clicked e.g. "Hypertension"
    searchId: The searchId of the search they performed
    type: The type of the result they clicked.
          One of: "differentAudience", "aiMatch", "matches", "fuzzyMatch"
    session_id: The session_id of the current session
 */
export function analyticsTrack_search_multiple_results_then_click(
  args: {
    term: string;
    persona: string | null | undefined;
    careCategory: string | null | undefined;
    patientGroup: string | null | undefined;
    ranking: number;
    title: string;
    slug: string;
    searchId: string;
    type: 'differentAudience' | 'aiMatch' | 'matches' | 'fuzzyMatch';
    search_source: string;
  },
  pearlUserInfo: PearlUser | undefined
) {
  analyticsTrack('search_multiple_results_then_click', args, pearlUserInfo);
}
